import * as Sentry from '@sentry/react'
import { AxiosError } from 'axios'

type ContextType = Record<string, unknown> & {
    source: string
}

const sanitize = (obj: Record<string, unknown>) => {
    try {
        return JSON.parse(JSON.stringify(obj)) as Record<string, unknown>
    } catch {
        return {}
    }
}

export const normalizeUrl = (url?: string): string => {
    if (!url) return 'UNKNOWN_URL'
    return url
        .split('?')[0] // remove query params
        .replace(
            /[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{12}/gi,
            ':id'
        ) // UUIDs
}

export const logError = (error: unknown, context: ContextType) => {
    if (error instanceof AxiosError) {
        // Skip timeout errors
        if (error.code === 'ECONNABORTED') return

        // Skip network errors if there's no response status
        if (error.code === 'ERR_NETWORK' && !error.response?.status) {
            return
        }

        // Skip offline errors
        if (typeof navigator !== 'undefined' && !navigator.onLine) return

        // Check if this is a login attempt
        // Login is specifically POST /api/v1/sessions
        const url = error.config?.url || ''
        const normalizedUrl = url.split('?')[0]
        const method = error.config?.method?.toLowerCase()
        const isLoginAttempt =
            normalizedUrl.endsWith('/api/v1/sessions') && method === 'post'

        // Skip 401 errors for non-login API calls
        if (error.response?.status === 401) return

        if (isLoginAttempt) {
            Sentry.setTag('error.type', 'login_failure')
            Sentry.setTag(
                'error.category',
                error.response?.status && error.response.status >= 500
                    ? 'infrastructure'
                    : 'authentication'
            )
        } else {
            // Tag 5xx server errors as infrastructure issues
            if (error.response?.status && error.response.status >= 500) {
                Sentry.setTag('error.category', 'infrastructure')
            }
        }

        Sentry.setTag(
            'http.status_code',
            error.response?.status.toString() || 'unknown'
        )
        Sentry.setTag(
            'http.trace_id',
            String(error.config?.headers['X-Trace-Id'] ?? 'none')
        )
        Sentry.setTag('http.method', error.config?.method ?? 'unknown')
        Sentry.setTag('http.url', error.config?.url ?? 'unknown')
    }

    Sentry.setTag('react-query.source', context.source)

    const fingerprint = ['react-query']

    if (error instanceof AxiosError) {
        const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN_METHOD'
        const url = normalizeUrl(error.config?.url)
        const status = error.response?.status.toString() ?? error.message

        fingerprint.push(status, method, url)
    } else if (error instanceof Error) {
        fingerprint.push(error.name, error.message)
    }

    Sentry.captureException(error, {
        extra: sanitize(context),
        fingerprint,
    })
}
